import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PencilIcon, EyeIcon, CopyIcon } from 'lucide-react';

import { queryClient } from 'api/queryClient';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { EditableSession } from 'types/event.types';
import { getEventDateString, getEventTimeString } from 'utils/eventUtils';
import { cn } from '@epoch-ui/lib/utils';
import { Badge } from '@epoch-ui/components/Badge';
import { Button } from '@epoch-ui/components/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { eventSessionsApi } from '@event-editor/api/eventSessions';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { guessTimezone } from 'utils/dateTimeUtils';

import { UpdateSessionSheet } from './UpdateSessionSheet';
import { getUpdateSessionSchema, sessionToUpdateFormValues } from './schemas';

interface EventSessionListItemProps extends React.HTMLAttributes<HTMLDivElement> {
  session: EditableSession;
}

const EventSessionListItem: React.FC<EventSessionListItemProps> = ({
  session,
  className,
  ...props
}) => {
  const parentEvent = useEditableEvent();
  const { showToast } = useToaster();

  const [updateSheetOpen, setUpdateSheetOpen] = useState(false);

  const duplicateSessionMutation = useMutation({
    mutationFn: eventSessionsApi.duplicate,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session duplicated');

        // Refresh sessions
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(parentEvent.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(parentEvent.id!)
        });
      } else {
        showToast('Failed to duplicate session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to duplicate session', { variant: ToasterVariant.ERROR });
    }
  });

  const isValid = useMemo(() => {
    if (parentEvent.isPublished && session.hasEnded) {
      return true;
    }
    const timezoneCode = parentEvent.timezoneCode || guessTimezone();

    return getUpdateSessionSchema(session, timezoneCode).safeParse(
      sessionToUpdateFormValues(session, timezoneCode)
    ).success;
  }, [session, parentEvent.isPublished, parentEvent.timezoneCode]);

  const isPast = parentEvent.isPublished && (session.hasEnded || parentEvent.hasEnded);

  return (
    <div
      {...props}
      className={cn(
        'tw-flex tw-flex-row tw-gap-4 tw-rounded-3xl tw-border tw-border-solid tw-p-4',
        className
      )}
    >
      <button
        type="button"
        disabled={isPast}
        className={cn(
          'tw-m-0 tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-p-0 tw-text-left',
          isPast && 'tw-opacity-40'
        )}
        onClick={() => setUpdateSheetOpen(true)}
      >
        <p
          className={cn(
            'tw-m-0 tw-text-sm tw-font-medium tw-leading-4 tw-text-foreground',
            !isValid && 'tw-text-destructive-foreground'
          )}
        >
          {session.title}
        </p>
        <p
          className={cn(
            'tw-m-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-muted-foreground',
            !isValid && 'tw-text-destructive-foreground'
          )}
        >
          {getEventDateString(session)}, {getEventTimeString(session)}
          {(session?.maxAttendees || 0) > 0 && ` • Capacity of ${session?.maxAttendees}`}
          {(session?.targetAttendance || 0) > 0 && ` • Target of ${session?.targetAttendance}`}
        </p>
      </button>

      {parentEvent.isPublished && <SessionStatusBadge session={session} />}
      {session.hasEnded ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="none" onClick={() => setUpdateSheetOpen(true)}>
              <EyeIcon size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Preview session</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="none" onClick={() => setUpdateSheetOpen(true)}>
              <PencilIcon size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit session</TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="none"
            loading={duplicateSessionMutation.isPending}
            disabled={!isValid || parentEvent.hasEnded || duplicateSessionMutation.isPending}
            onClick={() => duplicateSessionMutation.mutate({ sessionId: session.id! })}
          >
            <CopyIcon size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Duplicate session</TooltipContent>
      </Tooltip>

      <UpdateSessionSheet
        session={session}
        open={updateSheetOpen}
        onOpenChange={setUpdateSheetOpen}
      />
    </div>
  );
};

export { EventSessionListItem };

const SessionStatusBadge: React.FC<{ session: EditableSession }> = ({ session }) => {
  if (session.hasEnded) {
    return <Badge variant="secondary">Past session</Badge>;
  }

  if (session.isPublished) {
    return <Badge variant="positive">Published</Badge>;
  }

  return <Badge variant="secondary">Draft</Badge>;
};
